import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Resolver, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { addJob, getJobById, updateJob } from "@endpoints/job.service";
import { getSkills } from "@endpoints/skill.service";
import { JobStatus } from "@utils/constants";
import {
  getFirstInvalidStep,
  JOB_FORM_DEFAULTS,
  JobFormValues,
  STEP_COUNT,
  STEP_SCHEMAS,
} from "../schema/jobForm.schema";
import { fromJob, toJobPayload } from "../lib/jobPayload";
import { fromSkill } from "../lib/skillPayload";
import { getJobEdits } from "../lib/jobEditing";
import { useDispatch, useSelector } from "react-redux";
import { setJobDetail } from "../../../store/actions/job.action";

const clampStep = (step: number) =>
  Math.min(Math.max(Math.trunc(step) || 1, 1), STEP_COUNT);

export const useCreateJobWizard = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const userId = useSelector((state: any) => state.auth.user?._id);
  const [isEditing, setIsEditing] = useState(searchParams.get("mode") === "edit");
  const initialValues = useRef<JobFormValues | null>(null);
  const [loadError, setLoadError] = useState(false);

  // Captured once so a refresh resumes the saved draft at the same step.
  const initialParams = useRef({
    jobId: searchParams.get("jobId"),
    step: clampStep(Number(searchParams.get("step"))),
    editing: searchParams.get("mode") === "edit",
  });

  const [activeStep, setActiveStep] = useState(1);
  const [jobId, setJobId] = useState<string | null>(
    initialParams.current.jobId,
  );
  // Mirrors jobId synchronously so back-to-back saves never create two jobs.
  const jobIdRef = useRef(jobId);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingDraft, setIsLoadingDraft] = useState(
    Boolean(initialParams.current.jobId),
  );

  // Read by the resolver, and updated synchronously in goToStep so a
  // validation run right after a step change uses the new step's schema.
  const stepRef = useRef(activeStep);

  const stepResolver: Resolver<JobFormValues> = (values, context, options) =>
    zodResolver(STEP_SCHEMAS[stepRef.current - 1] as any, undefined, {
      raw: true,
    })(values, context, options as any) as any;

  const form = useForm<JobFormValues>({
    defaultValues: JOB_FORM_DEFAULTS,
    mode: "onTouched",
    resolver: stepResolver,
  });

  const goToStep = useCallback((step: number) => {
    stepRef.current = step;
    setActiveStep(step);
  }, []);

  useEffect(() => {
    if (isLoadingDraft || !jobId) return;
    setSearchParams({ jobId, step: String(activeStep), ...(isEditing ? { mode: "edit" } : {}) }, { replace: true });
  }, [jobId, activeStep, isLoadingDraft, setSearchParams, isEditing]);

  useEffect(() => {
    const { jobId: draftId, step } = initialParams.current;
    if (!draftId) return;
    let isCancelled = false;

    Promise.all([
      getJobById({ jobId: draftId }),
      getSkills({ jobId: draftId, type: 2 }),
    ])
      .then(([{ data }, skillsResponse]) => {
        if (isCancelled) return;
        if (!data?._id) throw new Error("Could not load the job");
        if ((data.createdBy?._id ?? data.createdBy) !== userId) throw new Error("You can only edit your own job posts");
        if (![JobStatus.DRAFT, JobStatus.LIVE, JobStatus.CLOSED, JobStatus.UNPUBLISHED].includes(data.status)) throw new Error("This job's current status does not allow editing");
        if (!skillsResponse?.success) throw new Error("Could not load the job skills");
        const editing = initialParams.current.editing || data.status !== JobStatus.DRAFT;
        setIsEditing(Boolean(editing));
        const values = fromJob(data);
        if (skillsResponse?.success && skillsResponse?.data?.length) {
          values.skills = skillsResponse.data.map(fromSkill);
        } else if (editing) {
          values.skills = [];
        }
        initialValues.current = values;
        form.reset(values);
        goToStep(editing ? Math.max(2, Math.min(step, 6)) : Math.min(step, getFirstInvalidStep(values) ?? STEP_COUNT));
      })
      .catch((error) => {
        if (!isCancelled) { setLoadError(true); enqueueSnackbar(String(error), { variant: "error" }); }
      })
      .finally(() => {
        if (!isCancelled) setIsLoadingDraft(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [form, goToStep, enqueueSnackbar, userId]);

  // Skills are saved separately, from each skill card (skill/add, skill/update).
  const persistDraft = async (values: JobFormValues) => {
    if (isEditing) throw new Error("Use Save changes to update an existing job");
    const payload = { ...toJobPayload(values), status: JobStatus.DRAFT };

    if (jobIdRef.current) {
      if (activeStep < 3) {
        delete payload.keyResponsibilities;
        delete payload.salaryAndBenefits;
      }
      const { success } = await updateJob({
        jobId: jobIdRef.current,
        ...payload,
      });
      if (!success) throw new Error("Could not save the job draft");
      return jobIdRef.current;
    }

    const { success, data } = await addJob(payload);
    if (!success || !data?._id) throw new Error("Could not save the job draft");
    jobIdRef.current = data._id;
    setJobId(data._id);
    return data._id as string;
  };

  // A skill needs a job to attach to; the job normally exists after Step 1.
  const ensureJobId = async () =>
    jobIdRef.current ?? persistDraft(form.getValues());

  const withSaving = async (action: () => Promise<void>) => {
    try {
      setIsSaving(true);
      await action();
    } catch (error) {
      enqueueSnackbar(error?.message ?? String(error), { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  // Moves forward only when every step before `target` is valid. The current
  // step is validated by handleSubmit so its errors render inline; earlier
  // steps are re-checked because the user may have edited them since.
  const goForwardTo = (target: number) =>
    form.handleSubmit((values) => {
      const invalidStep = getFirstInvalidStep(values, target - 1);
      if (invalidStep) {
        goToStep(invalidStep);
        form.trigger();
        return;
      }
      return withSaving(async () => {
        await persistDraft(values);
        goToStep(target);
      });
    })();

  const continueToNextStep = () => isEditing ? goToStep(Math.min(activeStep + 1, 6)) : goForwardTo(activeStep + 1);

  const goBack = () => {
    if (isEditing && activeStep === 2) navigate(`/job/${jobId}/job-details`);
    else if (activeStep === 1) navigate(-1);
    else goToStep(activeStep - 1);
  };

  const requestStep = (target: number) => {
    if (isSaving || target === activeStep) return;
    if (isEditing) { goToStep(Math.max(2, Math.min(target, 6))); return; }
    if (target < activeStep) goToStep(target);
    else goForwardTo(target);
  };

  // No validation: the backend accepts partial drafts.
  const saveDraft = () =>
    withSaving(async () => {
      await persistDraft(form.getValues());
      enqueueSnackbar("Draft saved", { variant: "success" });
    });

  const saveChanges = () => withSaving(async () => {
    if (isLoadingDraft || loadError || !jobIdRef.current || !initialValues.current) throw new Error("Load the job before saving changes");
    form.clearErrors();
    const { updates, errors } = getJobEdits(form.getValues(), initialValues.current);
    if (errors.length) {
      errors.forEach(error => form.setError(error.path as any, { type: "validate", message: error.message }));
      goToStep(errors[0].step);
      return;
    }
    if (Object.keys(updates).length) {
      const result = await updateJob({ jobId: jobIdRef.current, ...updates });
      if (!result.success) throw new Error("Could not save job changes");
    }
    const response = await getJobById({ jobId: jobIdRef.current });
    if (response?.data) dispatch(setJobDetail(response.data));
    enqueueSnackbar("Job changes saved", { variant: "success" });
    navigate(`/job/${jobIdRef.current}/job-details`, { replace: true });
  });

  // Saves the draft, lets Step 8 take the payment, then publishes the job.
  const publishJob = (processPayment: (jobId: string) => Promise<void>) =>
    form.handleSubmit((values) => {
      if (isEditing) return;
      const invalidStep = getFirstInvalidStep(values);
      if (invalidStep) {
        goToStep(invalidStep);
        form.trigger();
        return;
      }
      return withSaving(async () => {
        const savedJobId = await persistDraft(values);
        await processPayment(savedJobId);

        const { success } = await updateJob({
          jobId: savedJobId,
          status: JobStatus.LIVE,
        });
        if (!success) {
          throw new Error(
            "Payment went through, but the job could not be published",
          );
        }
        enqueueSnackbar("Payment made successfully", { variant: "success" });
        navigate("/job/by-job", { replace: true });
      });
    })();

  return {
    isEditing,
    loadError,
    saveChanges,
    form,
    jobId,
    activeStep,
    isSaving,
    isLoadingDraft,
    continueToNextStep,
    goBack,
    requestStep,
    saveDraft,
    publishJob,
    ensureJobId,
  };
};

export type CreateJobWizard = Omit<
  ReturnType<typeof useCreateJobWizard>,
  "form"
>;

export const CreateJobContext = createContext<CreateJobWizard | null>(null);

export const useCreateJob = () => {
  const context = useContext(CreateJobContext);
  if (!context) {
    throw new Error("useCreateJob must be used inside CreateJobPage");
  }
  return context;
};
